/**
 * @module lib/ai/ragSystem
 * @description Sistema RAG (Retrieval Augmented Generation) para el asistente IA de cumplimiento ISO.
 *
 * Enriquece los prompts enviados al LLM con contexto específico del control ISO consultado,
 * mejores prácticas de la base de conocimiento interna y tipos de evidencia recomendados.
 *
 * ## Arquitectura
 * En producción, el RAG debería conectarse a una base vectorial (Supabase pgvector,
 * Pinecone, Weaviate) para búsqueda semántica real. En la implementación actual,
 * usa una base de conocimiento estática (`ISO_KNOWLEDGE_BASE`) que se filtra
 * por similitud de palabras clave. Esto permite demostrar el flujo sin infraestructura
 * de embeddings.
 *
 * ## Funciones principales
 * | Función | Propósito |
 * |---------|-----------|
 * | `buildEnrichedContext()` | Construye contexto enriquecido con datos del control y RAG |
 * | `getBestPractices()` | Retorna mejores prácticas para la categoría del control |
 * | `getRecommendedEvidenceTypes()` | Lista tipos de evidencia por categoría ISO |
 * | `retrieveRelevantDocumentation()` | Búsqueda por similitud de palabras clave (mock) |
 * | `computeControlSimilarity()` | Score de relevancia entre control y query |
 *
 * ## Reemplazo por RAG real
 * Para usar embeddings reales, reemplazar `retrieveRelevantDocumentation()` con
 * una llamada a `supabase.rpc('match_documents', { query_embedding, threshold, count })`
 * usando la extensión `pgvector` de Supabase.
 *
 * @see assistantGemini.ts que consume este módulo para enriquecer los prompts
 * @see types/ai.ts para los tipos RAGResult y Embedding
 */
// =====================================================
// SISTEMA RAG - RETRIEVAL AUGMENTED GENERATION
// =====================================================

import type { ISOControl } from '../../types/iso';
import type { RAGResult } from '../../types/ai';

/**
 * Base de conocimiento de ISO 27001/27002
 * En producción esto vendría de una base vectorial (Supabase pgvector, Pinecone, etc.)
 */
const ISO_KNOWLEDGE_BASE = {
  // Documentación general
  general: [
    {
      topic: 'ISO 27001 Overview',
      content: `ISO 27001 es un estándar internacional para sistemas de gestión de seguridad de la información (SGSI).
      Define requisitos para establecer, implementar, mantener y mejorar continuamente un SGSI.
      La versión actual es ISO 27001:2022.`
    },
    {
      topic: 'ISO 27002 Controls',
      content: `ISO 27002 proporciona guías de mejores prácticas para controles de seguridad de la información.
      Contiene 93 controles organizados en 4 categorías: Organizacional, Personas, Física, y Tecnológica.`
    },
    {
      topic: 'Risk Assessment',
      content: `La evaluación de riesgos es fundamental en ISO 27001. Incluye:
      1. Identificación de activos de información
      2. Identificación de amenazas y vulnerabilidades
      3. Evaluación de impacto y probabilidad
      4. Determinación del nivel de riesgo
      5. Selección de controles apropiados`
    }
  ],

  // Tipos de evidencias recomendadas por categoría
  evidenceTypes: {
    'Organizational': [
      'Políticas de seguridad documentadas',
      'Procedimientos operativos',
      'Actas de reuniones del comité de seguridad',
      'Registros de revisiones de gestión',
      'Análisis de riesgos documentados'
    ],
    'People': [
      'Contratos de confidencialidad firmados',
      'Registros de capacitación en seguridad',
      'Evaluaciones de desempeño',
      'Certificados de formación',
      'Acuerdos de usuario aceptable'
    ],
    'Physical': [
      'Fotografías de áreas seguras',
      'Registros de control de acceso físico',
      'Logs de visitantes',
      'Contratos de mantenimiento de sistemas de seguridad física',
      'Planos con zonas de seguridad marcadas'
    ],
    'Technological': [
      'Capturas de pantalla de configuraciones',
      'Logs de sistemas',
      'Reportes de vulnerabilidades',
      'Certificados de cifrado',
      'Registros de auditoría técnica',
      'Reportes de backups'
    ]
  },

  // Mejores prácticas por tipo de control
  bestPractices: {
    'access_control': [
      'Implementar autenticación multifactor (MFA)',
      'Revisar permisos de acceso trimestralmente',
      'Aplicar principio de mínimo privilegio',
      'Mantener logs de acceso por al menos 90 días',
      'Documentar procedimientos de alta/baja de usuarios'
    ],
    'cryptography': [
      'Usar algoritmos criptográficos aprobados (AES-256, RSA-2048+)',
      'Gestionar ciclo de vida de claves criptográficas',
      'Cifrar datos en reposo y en tránsito',
      'Documentar política de criptografía',
      'Realizar auditorías de uso de cifrado'
    ],
    'physical_security': [
      'Implementar zonas de seguridad física',
      'Controlar acceso mediante tarjetas/biometría',
      'Instalar CCTV en áreas críticas',
      'Realizar revisiones periódicas de seguridad física',
      'Documentar procedimientos de visitantes'
    ],
    'operations_security': [
      'Establecer procedimientos de cambio',
      'Implementar gestión de capacidad',
      'Separar ambientes (dev/test/prod)',
      'Realizar backups regulares',
      'Documentar procedimientos operativos'
    ],
    'communications_security': [
      'Implementar políticas de transferencia de información',
      'Usar canales de comunicación seguros',
      'Establecer acuerdos de confidencialidad (NDA)',
      'Monitorear comunicaciones críticas',
      'Documentar protocolos de seguridad de red'
    ],
    'incident_management': [
      'Establecer procedimiento de respuesta a incidentes',
      'Definir roles y responsabilidades',
      'Mantener registro de incidentes',
      'Realizar post-mortems',
      'Entrenar al equipo en respuesta a incidentes'
    ]
  }
};

/**
 * Similarity search simplificado usando coincidencia de palabras clave
 * En producción usaríamos embeddings vectoriales reales
 */
function calculateSimilarity(text1: string, text2: string): number {
  const words1 = text1.toLowerCase().split(/\s+/);
  const words2 = text2.toLowerCase().split(/\s+/);

  const intersection = words1.filter(word => words2.includes(word));
  const union = new Set([...words1, ...words2]);

  return intersection.length / union.size;
}

/**
 * Buscar controles relevantes basados en consulta
 */
export function findRelevantControls(
  query: string,
  controls: ISOControl[],
  limit: number = 5
): RAGResult['relevantControls'] {
  const results = controls.map(control => {
    // Calcular similitud con título, descripción y categoría
    const titleSim = calculateSimilarity(query, control.title);
    const descSim = calculateSimilarity(query, control.description);
    const categorySim = calculateSimilarity(query, control.category);

    // Peso ponderado
    const similarity = (titleSim * 0.5) + (descSim * 0.3) + (categorySim * 0.2);

    return { control, similarity };
  });

  // Ordenar por similitud y retornar top N
  return results
    .filter(r => r.similarity > 0.1) // Umbral mínimo
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, limit);
}

/**
 * Buscar documentación relevante
 */
export function findRelevantDocumentation(
  query: string,
  limit: number = 3
): RAGResult['relevantDocumentation'] {
  const allDocs = ISO_KNOWLEDGE_BASE.general;

  const results = allDocs.map(doc => ({
    text: doc.content,
    source: doc.topic,
    similarity: calculateSimilarity(query, doc.content + ' ' + doc.topic)
  }));

  return results
    .filter(r => r.similarity > 0.05)
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, limit);
}

/**
 * Obtener contexto completo para RAG
 */
export function getRAGContext(
  query: string,
  controls: ISOControl[],
  currentControl?: ISOControl
): RAGResult {
  const relevantControls = findRelevantControls(query, controls);
  const relevantDocumentation = findRelevantDocumentation(query);

  // Si hay un control actual, agregarlo al principio con alta similitud
  if (currentControl && !relevantControls.find(rc => rc.control.id === currentControl.id)) {
    relevantControls.unshift({ control: currentControl, similarity: 1.0 });
  }

  return {
    relevantControls,
    relevantDocumentation
  };
}

/**
 * Obtener mejores prácticas para una categoría de control
 */
export function getBestPractices(category: string): string[] {
  const categoryKey = Object.keys(ISO_KNOWLEDGE_BASE.bestPractices).find(key =>
    category.toLowerCase().includes(key.replace(/_/g, ' '))
  );

  if (categoryKey) {
    return ISO_KNOWLEDGE_BASE.bestPractices[categoryKey as keyof typeof ISO_KNOWLEDGE_BASE.bestPractices];
  }

  // Retornar mejores prácticas generales si no hay match específico
  return [
    'Documentar todos los procedimientos claramente',
    'Realizar revisiones periódicas del control',
    'Mantener evidencias actualizadas',
    'Capacitar al personal involucrado',
    'Establecer métricas de efectividad'
  ];
}

/**
 * Obtener tipos de evidencia recomendados según categoría
 */
export function getRecommendedEvidenceTypes(category: string): string[] {
  // Mapeo de categorías ISO a nuestras categorías
  const categoryMapping: Record<string, keyof typeof ISO_KNOWLEDGE_BASE.evidenceTypes> = {
    'organizational': 'Organizational',
    'organizacional': 'Organizational',
    'people': 'People',
    'personas': 'People',
    'physical': 'Physical',
    'física': 'Physical',
    'fisico': 'Physical',
    'technological': 'Technological',
    'tecnológica': 'Technological',
    'tecnologica': 'Technological'
  };

  const normalizedCategory = category.toLowerCase();
  const matchedKey = Object.keys(categoryMapping).find(key =>
    normalizedCategory.includes(key)
  );

  if (matchedKey) {
    const evidenceKey = categoryMapping[matchedKey];
    return ISO_KNOWLEDGE_BASE.evidenceTypes[evidenceKey] || [];
  }

  // Evidencias genéricas si no hay match
  return [
    'Documentación de políticas y procedimientos',
    'Registros de implementación',
    'Evidencias de monitoreo y revisión',
    'Certificados o comprobantes de cumplimiento'
  ];
}

/**
 * Construir contexto enriquecido para prompt
 */
export function buildEnrichedContext(
  query: string,
  controls: ISOControl[],
  currentControl?: ISOControl
): string {
  const ragResult = getRAGContext(query, controls, currentControl);

  let context = '';

  // Agregar control actual si existe
  if (currentControl) {
    context += `\n### Control Actual:\n`;
    context += `- Código: ${currentControl.code}\n`;
    context += `- Título: ${currentControl.title}\n`;
    context += `- Descripción: ${currentControl.description}\n`;
    context += `- Categoría: ${currentControl.category}\n`;
    context += `- Estado: ${currentControl.status}\n`;
    context += `- Prioridad: ${currentControl.priority}\n`;

    // Agregar mejores prácticas para la categoría
    const bestPractices = getBestPractices(currentControl.category);
    if (bestPractices.length > 0) {
      context += `\n### Mejores Prácticas para ${currentControl.category}:\n`;
      bestPractices.forEach((practice, idx) => {
        context += `${idx + 1}. ${practice}\n`;
      });
    }

    // Agregar tipos de evidencia recomendados
    const evidenceTypes = getRecommendedEvidenceTypes(currentControl.category);
    if (evidenceTypes.length > 0) {
      context += `\n### Tipos de Evidencia Recomendados:\n`;
      evidenceTypes.forEach((type, idx) => {
        context += `${idx + 1}. ${type}\n`;
      });
    }
  }

  // Agregar controles relevantes
  if (ragResult.relevantControls.length > 0) {
    context += `\n### Controles Relacionados:\n`;
    ragResult.relevantControls.slice(0, 3).forEach(rc => {
      context += `- ${rc.control.code}: ${rc.control.title}\n`;
    });
  }

  // Agregar documentación relevante
  if (ragResult.relevantDocumentation.length > 0) {
    context += `\n### Documentación Relevante:\n`;
    ragResult.relevantDocumentation.forEach(doc => {
      context += `**${doc.source}**: ${doc.text}\n\n`;
    });
  }

  return context;
}
